import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useLanguage } from "../../context/LanguageProvider";
import { useSiteData } from "../../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../../lib/i18n";
import { domainOptionValue } from "../../lib/format";
import { Send, CheckCircle2 } from "lucide-react";

/**
 * Full contact form for the /contact page. source="contact", consent is
 * required. The `domain` field must post "burkina-canada" (not the practice
 * slug) for cross-border routing — that mapping happens in `domainOptionValue`.
 */
export function FullContactForm({ initialDomain }) {
    const { lang } = useLanguage();
    const { practiceAreas } = useSiteData();
    const [params] = useSearchParams();

    const preselect = initialDomain || params.get("domain") || "";

    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        domain: preselect,
        subject: "",
        message: "",
        consent: false,
        website: "", // honeypot
    });
    const [state, setState] = useState({ status: "idle", error: "" });

    useEffect(() => {
        if (preselect && !form.domain) {
            setForm((f) => ({ ...f, domain: preselect }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preselect]);

    const canSubmit =
        form.name.trim() &&
        form.email.trim() &&
        form.message.trim() &&
        state.status !== "submitting";

    const onChange = (name) => (e) => {
        const val =
            e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [name]: val }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        if (!form.consent) {
            setState({
                status: "error",
                error: pick(UI_STRINGS.form.consent_required, lang),
            });
            return;
        }
        setState({ status: "submitting", error: "" });
        try {
            await api.post("/contact", {
                source: "contact",
                name: form.name.trim(),
                phone: form.phone.trim() || undefined,
                email: form.email.trim(),
                domain: form.domain || undefined,
                subject: form.subject.trim() || undefined,
                message: form.message.trim(),
                consent: true,
                website: form.website,
            });
            setState({ status: "success", error: "" });
            setForm({
                name: "",
                phone: "",
                email: "",
                domain: "",
                subject: "",
                message: "",
                consent: false,
                website: "",
            });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : "";
            const msg =
                code === "CONSENT_REQUIRED"
                    ? pick(UI_STRINGS.form.consent_required, lang)
                    : pick(UI_STRINGS.form.error, lang);
            setState({ status: "error", error: msg });
        }
    };

    if (state.status === "success") {
        return (
            <div
                data-testid="full-form-success"
                className="border border-gold/60 bg-ink-surface p-8 flex items-start gap-4"
            >
                <CheckCircle2
                    className="text-gold shrink-0 mt-1"
                    size={26}
                    strokeWidth={1.5}
                />
                <div>
                    <div className="font-serif text-2xl text-neutral-50 mb-2">
                        {pick(
                            { fr: "Message envoyé", en: "Message sent" },
                            lang,
                        )}
                    </div>
                    <div className="text-sm text-neutral-300 leading-relaxed">
                        {pick(UI_STRINGS.form.success, lang)}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
            data-testid="full-contact-form"
            noValidate
        >
            <div
                className="absolute -left-[9999px] w-px h-px overflow-hidden"
                aria-hidden="true"
            >
                <label>
                    Website
                    <input
                        tabIndex={-1}
                        autoComplete="off"
                        type="text"
                        name="website"
                        value={form.website}
                        onChange={onChange("website")}
                    />
                </label>
            </div>

            <label className="block">
                <span className="eyebrow block mb-2">
                    {pick(UI_STRINGS.form.name, lang)} *
                </span>
                <input
                    type="text"
                    required
                    className="field-box"
                    value={form.name}
                    onChange={onChange("name")}
                    data-testid="full-form-name"
                />
            </label>
            <label className="block">
                <span className="eyebrow block mb-2">
                    {pick(UI_STRINGS.form.phone, lang)}
                </span>
                <input
                    type="tel"
                    className="field-box"
                    value={form.phone}
                    onChange={onChange("phone")}
                    data-testid="full-form-phone"
                />
            </label>
            <label className="block md:col-span-2">
                <span className="eyebrow block mb-2">
                    {pick(UI_STRINGS.form.email, lang)} *
                </span>
                <input
                    type="email"
                    required
                    className="field-box"
                    value={form.email}
                    onChange={onChange("email")}
                    data-testid="full-form-email"
                />
            </label>
            <label className="block md:col-span-2">
                <span className="eyebrow block mb-2">
                    {pick(UI_STRINGS.form.domain, lang)}
                </span>
                <select
                    className="field-box"
                    value={form.domain}
                    onChange={onChange("domain")}
                    data-testid="full-form-domain"
                >
                    <option value="">
                        {pick(UI_STRINGS.form.domain_placeholder, lang)}
                    </option>
                    {practiceAreas.map((pa) => (
                        <option
                            key={pa.slug}
                            value={domainOptionValue(pa.slug)}
                        >
                            {pick(pa.title, lang)}
                        </option>
                    ))}
                </select>
            </label>
            <label className="block md:col-span-2">
                <span className="eyebrow block mb-2">
                    {pick(UI_STRINGS.form.subject, lang)}
                </span>
                <input
                    type="text"
                    className="field-box"
                    value={form.subject}
                    onChange={onChange("subject")}
                    data-testid="full-form-subject"
                />
            </label>
            <label className="block md:col-span-2">
                <span className="eyebrow block mb-2">
                    {pick(UI_STRINGS.form.message, lang)} *
                </span>
                <textarea
                    rows={6}
                    required
                    className="field-box resize-y"
                    value={form.message}
                    onChange={onChange("message")}
                    data-testid="full-form-message"
                />
            </label>

            <label
                className="md:col-span-2 flex items-start gap-3 cursor-pointer text-sm text-neutral-300"
                data-testid="full-form-consent-label"
            >
                <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={onChange("consent")}
                    className="mt-1 w-4 h-4 accent-[#D4AF37]"
                    data-testid="full-form-consent"
                    required
                />
                <span className="leading-relaxed">
                    {pick(UI_STRINGS.form.consent, lang)}
                </span>
            </label>

            {state.status === "error" && (
                <div
                    className="md:col-span-2 text-sm text-red-300 border border-red-900 bg-red-950/40 px-3 py-2"
                    data-testid="full-form-error"
                >
                    {state.error || pick(UI_STRINGS.form.error, lang)}
                </div>
            )}

            <button
                type="submit"
                disabled={!canSubmit || !form.consent}
                className="btn-gold md:col-span-2 justify-self-start"
                data-testid="full-form-submit"
            >
                {state.status === "submitting"
                    ? pick(UI_STRINGS.cta.sending, lang)
                    : pick(UI_STRINGS.cta.send, lang)}
                {state.status !== "submitting" && <Send size={16} />}
            </button>
        </form>
    );
}
